// TikTokEmbed.jsx
import { useEffect, useMemo, useRef } from "react";

/** Load embed.js once */
function ensureTikTokScript() {
  if (document.querySelector('script[src^="https://www.tiktok.com/embed.js"]')) return;
  const s = document.createElement("script");
  s.src = "https://www.tiktok.com/embed.js";
  s.async = true;
  document.body.appendChild(s);
}

/** Return a canonical TikTok URL if it's embeddable by the script. */
function pickEmbeddableUrl(webUrl) {
  if (!webUrl) return null;
  try {
    const u = new URL(webUrl);
    const host = u.hostname.toLowerCase();
    const path = u.pathname || "";
    const isTikTok =
      /(^|\.)tiktok\.com$/.test(host) || host === "vm.tiktok.com";
    const looksEmbedable =
      /^\/@[^/]+\/video\/\d+/.test(path) ||            // /@user/video/<id>
      (host === "vm.tiktok.com") ||                    // short link
      /^\/t\/[^/]+/.test(path);                        // /t/<share_id>
    if (isTikTok && looksEmbedable) {
      u.protocol = "https:"; // normalize protocol
      return u.toString();
    }
  } catch {
    /* ignore */
  }
  return null;
}

export default function TikTokEmbed({ video }) {
  const ref = useRef(null);
  const id = video?.id;
  const webUrlRaw = video?.metadata?.web_url || video?.metadata?.webVideoUrl;
  const embeddableUrl = useMemo(() => pickEmbeddableUrl(webUrlRaw), [webUrlRaw]);

  // 1) Load the script once
  useEffect(() => {
    ensureTikTokScript();
  }, []);

  // 2) Build/refresh the blockquote when we have a good URL
  useEffect(() => {
    if (!embeddableUrl || !id || !ref.current) return;

    // Clear previous embed, build blockquote TikTok expects
    ref.current.innerHTML = "";
    const bq = document.createElement("blockquote");
    bq.className = "tiktok-embed";
    bq.setAttribute("cite", embeddableUrl);
    bq.setAttribute("data-video-id", String(id));
    const a = document.createElement("a");
    a.href = embeddableUrl;
    bq.appendChild(a);
    ref.current.appendChild(bq);

    // Trigger TikTok's loader
    const trigger = () => {
      try {
        window.tiktokEmbedLoad?.();
        window.tiktok?.embed?.load?.();
      } catch {}
    };
    if (!(window.tiktokEmbedLoad || window.tiktok?.embed?.load)) {
      const t = setTimeout(trigger, 200);
      return () => clearTimeout(t);
    }
    trigger();
  }, [embeddableUrl, id]);

  // 3) Render
  if (!id) return null;

  // If no embeddable page URL, use iframe fallback that works with just an ID
  if (!embeddableUrl) {
    return (
      <div
        key={id}
        style={{
          position: "relative",
          width: "100%",
          // Default to vertical video; adjust if you prefer:
          paddingTop: "177.78%", // 9:16 -> 16/9 = 1.7778
        }}
      >
        <iframe
          title={`tiktok-${id}`}
          src={`https://www.tiktok.com/embed/v2/${id}`}
          allow="encrypted-media; fullscreen; clipboard-write"
          allowFullScreen
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    );
  }

  // Otherwise, render a container for the script to transform
  return <div ref={ref} key={id} />;
}

